import { usePostTransactions } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { currencyCodes } from '@fragment/types/currencyCodes';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { PaymentsModal } from './PaymentsModal';

/** Strip commas from a formatted amount string. */
const stripCommas = (s: string): string => s.replace(/,/g, '');

/** Convert a dollar amount string to integer cents string, preserving sign. */
const dollarsToCents = (dollars: string, precision: number): string => {
  const num = parseFloat(stripCommas(dollars));
  if (Number.isNaN(num)) return '0';
  return String(Math.round(num * 10 ** precision));
};

/** Add thousand-separator commas to the integer part of a numeric string. */
const addCommas = (intPart: string): string => {
  if (intPart.length <= 3) return intPart;
  const chars = intPart.split('');
  const result: string[] = [];
  chars.reverse().forEach((ch, i) => {
    if (i > 0 && i % 3 === 0) result.push(',');
    result.push(ch);
  });
  return result.reverse().join('');
};

/** Format an amount string as the user types: allow digits, one decimal point, and optional leading minus. */
const formatAmountInput = (raw: string): string => {
  const negative = raw.startsWith('-');
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  const intPart = addCommas(parts[0]);
  const formatted =
    parts.length <= 1
      ? intPart
      : `${intPart}.${parts.slice(1).join('').slice(0, 2)}`;
  return negative ? `-${formatted}` : formatted;
};

type SyncTransactionModalProps = {
  accountId: string;
  onClose: VoidFunction;
};

export const SyncTransactionModal = ({
  accountId,
  onClose,
}: SyncTransactionModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postTransactionMutation = usePostTransactions();

  const [externalId, setExternalId] = useState('');
  const [posted, setPosted] = useState(new Date().toISOString().split('T')[0]);
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [amount, setAmount] = useState('');

  const canSubmit =
    externalId.trim() !== '' &&
    posted.trim() !== '' &&
    amount.trim() !== '' &&
    !Number.isNaN(Number(stripCommas(amount))) &&
    !postTransactionMutation.isPending;

  const handleSubmit = useCallback(async () => {
    try {
      await postTransactionMutation.mutateAsync({
        data: {
          external_id: externalId,
          account: { id: accountId },
          posted: new Date(posted).toISOString(),
          currency: currencyCode as 'USD' | 'EUR' | 'GBP',
          amount: dollarsToCents(amount, 2),
          allocations: [],
        },
      });

      await queryClient.invalidateQueries({
        queryKey: ['/transactions'],
      });

      showToast(
        <Toast type="success" message="Transaction synced successfully" />
      );

      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to sync transaction. Please try again.';
      showToast(
        <Toast
          type="error"
          message={`Failed to sync transaction: ${errorMessage}`}
        />
      );
    }
  }, [
    postTransactionMutation,
    externalId,
    accountId,
    posted,
    currencyCode,
    amount,
    queryClient,
    showToast,
    onClose,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.shiftKey && e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
        handleSubmit();
      }
    },
    [canSubmit, handleSubmit]
  );

  const currencyOptions = useMemo(
    () =>
      currencyCodes
        .filter((code) => code !== 'LOGICAL' && code !== 'CUSTOM')
        .map((code) => ({ value: code, label: code })),
    []
  );

  return (
    <PaymentsModal
      data-testid="sync-transaction-modal"
      onClose={onClose}
      width="sm"
      shouldCenter={false}
      header={
        <div className="relative flex items-center justify-center w-full text-main text-xmd font-helvetica">
          <span>Sync Transaction</span>
        </div>
      }
      body={
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- grandfathered
        <div
          className="flex flex-col w-full text-xmd font-helvetica"
          onKeyDown={handleKeyDown}
        >
          {/* External ID */}
          <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-main-200">
            <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
              External ID
            </span>
            <div className="flex-1 flex items-stretch bg-main-100">
              <TextInput
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
                placeholder="External ID"
                size="xmd"
                width="w-full"
                height="h-full"
                padding="px-f1"
                align="left"
                background="!bg-main-100"
                extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
                data-testid="external-id-input"
              />
            </div>
          </div>

          {/* Posted */}
          <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
            <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
              Posted
            </span>
            <div className="flex-1 flex items-stretch bg-main-100">
              <TextInput
                value={posted}
                onChange={(e) => setPosted(e.target.value)}
                placeholder="YYYY-MM-DD"
                size="xmd"
                width="w-full"
                height="h-full"
                padding="px-f1"
                align="left"
                background="!bg-main-100"
                extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
                data-testid="posted-input"
              />
            </div>
          </div>

          {/* Amount */}
          <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
            <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
              Amount
            </span>
            <div className="flex-1 flex items-stretch bg-main-100">
              <TextInput
                value={amount}
                onChange={(e) => setAmount(formatAmountInput(e.target.value))}
                onBlur={() => {
                  const num = parseFloat(stripCommas(amount));
                  if (!Number.isNaN(num)) {
                    setAmount(formatAmountInput(num.toFixed(2)));
                  }
                }}
                placeholder="100.00"
                size="xmd"
                width="w-full"
                height="h-full"
                padding="px-f1"
                align="left"
                background="!bg-main-100"
                extraClassNames="!hover:bg-main-100 !focus:bg-main-100"
                data-testid="amount-input"
              />
            </div>
          </div>

          {/* Currency */}
          <div className="flex flex-row items-stretch h-f4 border-[0.5px] border-t-0 border-main-200">
            <span className="shrink-0 w-[8lh] px-f1 text-main-500 border-r-[0.5px] border-main-200 flex items-center">
              Currency
            </span>
            <div className="relative flex-1 flex items-center bg-main-100">
              <Dropdown
                options={currencyOptions}
                onChange={(val: string) => setCurrencyCode(val)}
                displayValue={currencyCode}
                overrideDisplayName={
                  <span className="flex items-center w-full">
                    <span>{currencyCode}</span>
                    <span className="font-FragmentMono text-main-500 ml-auto">
                      ↓
                    </span>
                  </span>
                }
                showIcon={false}
                backgroundColor="bg-main-100"
                hoverBackgroundColor="bg-negative"
                shadow="shadow-lg"
                size="xmd"
                width="w-full"
                height="h-f4"
                padding="px-f1"
                align="left"
                data-testid="currency-select"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-row gap-x-[0.25lh] pt-f2">
            <SecondaryButtonV3
              onClick={onClose}
              className="!text-xmd"
              data-testid="sync-cancel-button"
            >
              Cancel
            </SecondaryButtonV3>
            <PrimaryButtonV3
              // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="sync-submit-button"
            >
              {postTransactionMutation.isPending ? 'Syncing...' : 'Sync'}
            </PrimaryButtonV3>
          </div>
        </div>
      }
    />
  );
};
